import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ImagePlus, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export interface AttachedImage {
  file: File;
  preview: string;
  base64: string;
  filename: string;
  mimeType: string;
}

interface ChatImageAttachmentProps {
  attachedImages: AttachedImage[];
  onImagesChange: (images: AttachedImage[]) => void;
  disabled?: boolean;
  maxImages?: number;
  compact?: boolean;
}

export function ChatImageAttachment({
  attachedImages,
  onImagesChange,
  disabled = false,
  maxImages = 5,
  compact = false,
}: ChatImageAttachmentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const canRemove = !disabled && !isProcessing;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - attachedImages.length;
    if (remainingSlots <= 0) {
      toast({
        title: "Maximum images reached",
        description: `You can only attach up to ${maxImages} images.`,
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    const newImages: AttachedImage[] = [];

    for (const file of filesToProcess) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image.`,
          variant: "destructive",
        });
        continue;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 5MB limit.`,
          variant: "destructive",
        });
        continue;
      }

      try {
        const base64 = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        
        newImages.push({
          file,
          preview,
          base64,
          filename: file.name,
          mimeType: file.type,
        });
      } catch (error) {
        console.error("Error processing image:", error);
        toast({
          title: "Error processing image",
          description: `Failed to process ${file.name}.`,
          variant: "destructive",
        });
      }
    }

    if (newImages.length > 0) {
      onImagesChange([...attachedImages, ...newImages]);
    }

    setIsProcessing(false);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    if (!canRemove) return;
    const newImages = [...attachedImages];
    // Revoke object URL to prevent memory leaks
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get pure base64
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Expose processFiles for external use (drag-drop, paste)
  const processFiles = async (files: File[]): Promise<AttachedImage[]> => {
    const remainingSlots = maxImages - attachedImages.length;
    if (remainingSlots <= 0) {
      toast({
        title: "Maximum images reached",
        description: `You can only attach up to ${maxImages} images.`,
        variant: "destructive",
      });
      return [];
    }

    const filesToProcess = files.slice(0, remainingSlots);
    const newImages: AttachedImage[] = [];

    for (const file of filesToProcess) {
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image.`,
          variant: "destructive",
        });
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 5MB limit.`,
          variant: "destructive",
        });
        continue;
      }

      try {
        const base64 = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        
        newImages.push({
          file,
          preview,
          base64,
          filename: file.name,
          mimeType: file.type,
        });
      } catch (error) {
        console.error("Error processing image:", error);
        toast({
          title: "Error processing image",
          description: `Failed to process ${file.name}.`,
          variant: "destructive",
        });
      }
    }

    return newImages;
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Preview Bar */}
      {attachedImages.length > 0 && (
        <div className={`flex gap-2 ${compact ? "flex-wrap" : "overflow-x-auto"} pb-1`}>
          {attachedImages.map((img, index) => (
            <div
              key={index}
              className={`relative group shrink-0 ${canRemove ? "" : "opacity-70"}`}
            >
              <img
                src={img.preview}
                alt={img.filename}
                className={`${compact ? "h-12 w-12" : "h-16 w-16"} object-cover rounded-md border border-border`}
              />
              <button
                onClick={() => removeImage(index)}
                className={`absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center transition-opacity ${
                  canRemove ? "opacity-0 group-hover:opacity-100" : "opacity-0 pointer-events-none"
                }`}
                type="button"
                disabled={!canRemove}
                aria-disabled={!canRemove}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attachment Button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isProcessing}
      />
      
      <Button
        type="button"
        variant="ghost"
        size={compact ? "sm" : "icon"}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || isProcessing || attachedImages.length >= maxImages}
        className={compact ? "h-8 w-8 p-0" : "shrink-0"}
        title={`Attach images (${attachedImages.length}/${maxImages})`}
      >
        <ImagePlus className={compact ? "h-4 w-4" : "h-4 w-4"} />
      </Button>
    </div>
  );
}

export function useImageAttachment(maxImages = 5) {
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);

  const clearImages = () => {
    // Revoke all object URLs
    attachedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setAttachedImages([]);
  };

  const getImageAttachments = () => {
    return attachedImages.map((img) => ({
      base64: img.base64,
      filename: img.filename,
      mimeType: img.mimeType,
    }));
  };

  // Helper to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Process files from drag-drop or paste
  const addImagesFromFiles = async (files: File[]) => {
    const remainingSlots = maxImages - attachedImages.length;
    if (remainingSlots <= 0) {
      toast({
        title: "Maximum images reached",
        description: `You can only attach up to ${maxImages} images.`,
        variant: "destructive",
      });
      return;
    }

    const imageFiles = files.filter(f => f.type.startsWith("image/")).slice(0, remainingSlots);
    const newImages: AttachedImage[] = [];

    for (const file of imageFiles) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 5MB limit.`,
          variant: "destructive",
        });
        continue;
      }

      try {
        const base64 = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        
        newImages.push({
          file,
          preview,
          base64,
          filename: file.name,
          mimeType: file.type,
        });
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }

    if (newImages.length > 0) {
      setAttachedImages(prev => [...prev, ...newImages]);
    }
  };

  return {
    attachedImages,
    setAttachedImages,
    clearImages,
    getImageAttachments,
    addImagesFromFiles,
  };
}
